import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  Alert,
  Box,
  Container,
  Grid,
  GridCell,
  Image,
  Loading,
  Text
} from '../../../components';
import Link from '../../../components/Link';
import {
  CurrentUser,
  PaymentMethodType,
  productBySlugQuery_productBySlug,
  settings_system_paymentProvider
} from '../../../core-types';
import { ApolloError } from '@apollo/client';
import { CheckoutStep } from './CheckoutStep';
import { messages } from './messages';
import { globalMessages } from '../../../lib/messages';
import { GraphQLErrors } from '../../../components/GraphQLErrors';
import router from 'next/router';
import { hideproductSlugFromRequest } from '../../../lib/utils';

const OverflowImage = styled.div`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    overflow: hidden;
    height: 200px;
    border-radius: 10px;
    img {
      margin-top: -70px;
    }
  }
`;

const SummaryStep = ({
  product,
  urlQuery,
  currentUser,
  currentUserLoading,
  paymentProvider,
  availablePaymentMethods
}: {
  product: productBySlugQuery_productBySlug;
  productSlug: string;
  urlQuery: { cancelled?: string };
  currentUser: CurrentUser | null;
  currentUserLoading: boolean;
  paymentProvider?: settings_system_paymentProvider;
  availablePaymentMethods: PaymentMethodType[];
}) => {
  const [requestError, setRequestError] = useState<ApolloError | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  //This is a hack for ATB/OBOS campaigns. Remove when campaign is over.
  useEffect(() => {
    const productSlug = router?.query.productSlug;
    hideproductSlugFromRequest(productSlug);
  }, []);

  //Since we added the user consent update before checkout, we don't need this anymore
  // useEffect(() => {
  //   if (!currentUserLoading && (!agreedToTerms || !currentUser)) {
  //     const timeoutId = setTimeout(() => {
  //       replaceRoute({
  //         intl,
  //         route: 'buy',
  //         params: {
  //           productSlug
  //         }
  //       });
  //     }, 1500);
  //     return () => {
  //       clearTimeout(timeoutId);
  //     };
  //   }
  // }, [agreedToTerms, currentUser, currentUserLoading]);

  if (currentUserLoading) {
    return <Loading />;
  }

  if (!currentUser) {
    return (
      <Alert variant="error" my="sm" data-testid="SummaryStep_No_Token">
        <FormattedMessage
          id="modules.BuyProduct.SummaryStep.missingToken"
          defaultMessage="Missing verification token, please wait …"
        />
      </Alert>
    );
  }

  //Since we added the user consent update before checkout, we don't need this anymore
  // if (!agreedToTerms) {
  //   return (
  //     <Alert variant="error" my="sm" data-testid="SummaryStep_No_Tos_Accept">
  //       <FormattedMessage
  //         id="modules.BuyProduct.SummaryStep.missingTosAccept"
  //         defaultMessage="Missing terms of service acceptance, please wait …"
  //       />
  //     </Alert>
  //   );
  // }

  if (!product) {
    return (
      <Alert variant="error" my="sm">
        <FormattedMessage
          id="modules.BuyProduct.SummaryStep.missingProduct"
          defaultMessage="This product is not available"
        />
      </Alert>
    );
  }

  const isCancelled = urlQuery && urlQuery.cancelled;
  const disclaimer = (
    <Text mb="xs">
      <FormattedMessage
        id="modules.BuyProduct.SummaryStep.completePurchaseText"
        defaultMessage="By continuing, you accept the {termsOfPurchase} and cannot undo this purchase."
        values={{
          termsOfPurchase: (
            <Link route="terms-of-purchase">
              <a target="blank">
                <FormattedMessage {...messages.termsOfPurchase} />
              </a>
            </Link>
          )
        }}
      />
    </Text>
  );

  const productImageUrl = product.image && product.image.url;

  return (
    <Container variant="wide">
      {paymentError && (
        <Alert variant="error">
          <FormattedMessage {...globalMessages.genericError} />
        </Alert>
      )}
      {(requestError || isCancelled) && (
        <Grid justifyContent="center" alignItems="center">
          <GridCell width={[1, 1, 1, 3 / 5]}>
            {requestError && <GraphQLErrors error={requestError} />}

            {isCancelled && (
              <Alert
                variant="error"
                my="sm"
                data-testid="SummaryStep_Payment_Cancelled"
              >
                <FormattedMessage
                  id="modules.BuyProduct.SummaryStep.paymentCancelled"
                  defaultMessage="The payment was cancelled!"
                />
              </Alert>
            )}
          </GridCell>
        </Grid>
      )}

      <Grid justifyContent="center" alignItems="center">
        {productImageUrl && (
          <GridCell width={{ _: 1, lg: 2 / 5 }}>
            <OverflowImage>
              <Image
                boxShadow="heavy"
                borderRadius="lg"
                src={productImageUrl}
              />
            </OverflowImage>
          </GridCell>
        )}

        <GridCell
          width={productImageUrl ? { _: 1, lg: 3 / 5 } : undefined}
          px={productImageUrl ? { _: 3, lg: 6 } : undefined}
          flexDirection="column"
          height="100%"
        >
          <Box
            flex={1}
            mb="md"
            textAlign={
              product.image && product.image.url
                ? ['center', 'center', 'left']
                : 'center'
            }
          >
            <Text mb="xs" typoStyle="xs" mt={[4, 4, 4, 0]}>
              <FormattedMessage id="summary" defaultMessage="Summary" />
            </Text>
            {paymentProvider && (
              <CheckoutStep
                paymentProvider={paymentProvider}
                currentUser={currentUser}
                product={product}
                setRequestError={setRequestError}
                setPaymentError={setPaymentError}
                disclaimer={disclaimer}
                haveImage={Boolean(productImageUrl)}
                availablePaymentMethods={availablePaymentMethods}
              />
            )}
          </Box>
        </GridCell>
      </Grid>
    </Container>
  );
};

export default SummaryStep;
